import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useParams } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { DiaryDayRow, DiaryDaySheet } from "../components/CycleDiary";
import { CycleSheet } from "../components/CycleSheet";
import { CycleStrips } from "../components/CycleStrips";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { Notice } from "../components/Notice";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { cycleInfo, cycleOn } from "../domain/cycle";
import { PATTERN_ROWS, cycleHistory, shows, stripsFor, symptomTallies, type PatternRow } from "../domain/cyclePatterns";
import {
  CARE,
  CARE_WORDS,
  DIARY_WORDS,
  PATTERN_WORDS,
  SYMPTOM_LABEL,
  expectedBasedOn,
  expectedSentence,
  notYetLine,
  patternSentence,
  symptomHeroLine,
  typicalWord,
} from "../domain/cycleWords";
import { daysBetween, todayStr } from "../domain/date";
import { useGo } from "../lib/useGo";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";
import "./ProgressCycle.css";
import "./ProgressCycleSymptom.css";

/** The days logged shown before "Show all". */
const FIRST_DAYS = 12;

/**
 * /progress/cycle/diary/:row — one symptom across her cycles (ADR-060): the strips, the pattern
 * when one holds, what helps, and every day it was logged. What helps is words to read: nothing
 * here changes a session — when she logs the symptom on the day, ADR-057's offers apply.
 */
export default function ProgressCycleSymptom() {
  const S = useKaya((s) => s.S);
  const go = useGo();
  const params = useParams<{ row?: string }>();
  const [sheetDs, setSheetDs] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const today = todayStr();
  const back = { to: "/progress/cycle/diary", label: "Back to Symptom diary" };
  const row = PATTERN_ROWS.find((r) => r === params.row) as PatternRow | undefined;

  if (!cycleOn(S) || !row)
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title={row ? SYMPTOM_LABEL[row] : DIARY_WORDS.title} back={back} />
            <div className="pg">
              {cycleOn(S) ? (
                <Notice tone="info" secondary={{ label: DIARY_WORDS.title, onClick: () => go("/progress/cycle/diary") }}>
                  There is no symptom by that name in the diary.
                </Notice>
              ) : (
                <Notice tone="info" secondary={{ label: "Open You › Cycle", onClick: () => go("/you/cycle") }}>
                  Cycle tracking is off. Turn it on in You › Cycle and this page fills in.
                </Notice>
              )}
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );

  const label = SYMPTOM_LABEL[row];
  const tally = symptomTallies(S, today).find((t) => t.row === row) ?? null;
  const pattern = tally?.pattern ?? null;
  const notYet = tally?.notYet ?? null;
  const strips = tally ? stripsFor(S, today, row) : null;
  const last = cycleInfo(S, today)?.last;
  const day = last ? daysBetween(last, today) + 1 : null;
  const care = CARE[row];
  const days = cycleHistory(S, today)
    .flatMap((c) => c.days.filter((d) => shows(d.keys, row)).map((d) => ({ c, d })))
    .reverse();
  const first = days.slice(0, FIRST_DAYS);
  const rest = days.slice(FIRST_DAYS);
  const said = pattern ? patternSentence(pattern) : null;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title={label} back={back} />
          <div className="pg">
            <Hero
              label={label}
              figure={tally ? tally.days : null}
              unit="days"
              line={
                tally
                  ? symptomHeroLine(tally.cycles, tally.of, pattern ? typicalWord(row, pattern.typical) : null)
                  : `${DIARY_WORDS.empty}`
              }
              visual={strips ? <CycleStrips strips={strips} /> : undefined}
            />

            {pattern && said ? (
              <RowGroup heading={PATTERN_WORDS.one}>
                <Insight first insight={{ key: "pattern.window", text: said.text, basedOn: said.basedOn }} />
                {pattern.expected && last ? (
                  <Insight
                    insight={{
                      key: "pattern.expected",
                      text: expectedSentence(pattern, day),
                      basedOn: expectedBasedOn(last),
                    }}
                  />
                ) : null}
              </RowGroup>
            ) : notYet ? (
              <RowGroup heading={PATTERN_WORDS.one}>
                <Insight first insight={{ key: "pattern.wait", ...notYetLine(notYet) }} />
              </RowGroup>
            ) : null}

            <div className="pcs-care">
              <RowGroup heading={CARE_WORDS.heading}>
                <div className="pc-panel__cell">
                  <ul className="pc-panel__list">
                    {care.do.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </div>
                {care.food ? <NavRow title={CARE_WORDS.food} subtitle={care.food} icon={I.bowl} to="/food" /> : null}
                {care.move ? <NavRow title={CARE_WORDS.move} subtitle={care.move} icon={I.dumbbell} to="/train" /> : null}
              </RowGroup>
            </div>
            {care.gp ? (
              <Notice tone="info" title={CARE_WORDS.gp}>
                {care.gp}.
              </Notice>
            ) : null}

            {days.length ? (
              <div className="pcs-days">
                <RowGroup heading={DIARY_WORDS.daysLogged(days.length)}>
                  {first.map(({ c, d }) => (
                    <DiaryDayRow key={d.ds} c={c} d={d} onOpen={setSheetDs} />
                  ))}
                  {rest.length ? (
                    <DiscloseRow title={DIARY_WORDS.showAll} value={`${rest.length} more`} flush>
                      {rest.map(({ c, d }) => (
                        <DiaryDayRow key={d.ds} c={c} d={d} onOpen={setSheetDs} />
                      ))}
                    </DiscloseRow>
                  ) : null}
                </RowGroup>
              </div>
            ) : null}
          </div>

          <ActionBar
            secondary={{ label: DIARY_WORDS.another, onClick: () => setPicking(true) }}
            primary={{ label: DIARY_WORDS.logToday, icon: I.plus, onClick: () => setSheetDs(today) }}
          />
        </Screen>

        {sheetDs ? <CycleSheet open onClose={() => setSheetDs(null)} ds={sheetDs} /> : null}
        {picking ? (
          <DiaryDaySheet
            onClose={() => setPicking(false)}
            onOpen={(ds) => {
              setPicking(false);
              setSheetDs(ds);
            }}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
